/*
 * Rewards points row (cart page order summary) — mirrors the loyalty balance the Appstle Loyalty
 * app renders in the cart drawer.
 *
 * The app owns balances: it injects #appstle-loyalty-redeem-widget-block by its own selector list,
 * which matches the drawer's CTA block but nothing in the cart page's summary card. The drawer is
 * in the document on the cart page too (theme.liquid renders it on every page), so the balance the
 * app wrote there is what this reads — no second source of truth, and nothing calculated here.
 *
 * A custom element rather than a one-shot script: cart.js replaces the summary's .js-contents
 * innerHTML on every quantity change, so this element is constructed fresh each time and re-runs
 * its own setup, while script tags inserted that way would never execute again.
 *
 * The row stays hidden unless a balance is found, so an app embed that is off, or an app version
 * whose markup changed, leaves no empty panel behind.
 */
if (!customElements.get('cart-points-row')) {
  customElements.define(
    'cart-points-row',
    class CartPointsRow extends HTMLElement {
      /* Exact class first, then looser fallbacks for future app versions — same order of
         preference as the trigger list in pi-rewards-popup.js. */
      static BALANCE_SELECTORS = [
        '.loyalty-cart-widget-available-balance-label',
        '[class*="loyalty" i][class*="balance" i]',
        '[id*="appstle" i][id*="loyalt" i] [class*="balance" i]',
      ];

      /* The app embed boots after the theme, so the widget usually lands a moment later. Same
         window and interval as pi-rewards-popup.js: they are waiting on the same app. */
      static WAIT_MS = 10000;
      static POLL_MS = 400;

      connectedCallback() {
        this.labelTarget = this.querySelector('[data-mrc-points-label]');

        // A merchant who has pointed the app's own widget at the summary card gets that widget,
        // skinned by section-cart-page.css — two balance rows in one card is worse than none.
        if (document.querySelector('.cart-summary-wrapper #appstle-loyalty-redeem-widget-block')) return;

        const rewardsButton = this.querySelector('[data-pi-rewards]');
        if (rewardsButton && window.piRewardsPopup) window.piRewardsPopup.watch(rewardsButton);

        this.sync();
      }

      disconnectedCallback() {
        this.stopPolling();
        if (this.observer) this.observer.disconnect();
      }

      findBalance() {
        for (let i = 0; i < CartPointsRow.BALANCE_SELECTORS.length; i++) {
          let node;

          try {
            node = document.querySelector(CartPointsRow.BALANCE_SELECTORS[i]);
          } catch (error) {
            continue; // a selector this browser rejects must not stop the rest
          }

          // Never read our own copy of the figure back in.
          if (node && !this.contains(node) && node.textContent.trim() !== '') return node;
        }

        return null;
      }

      sync() {
        const balance = this.findBalance();

        if (balance) {
          this.paint(balance);
          this.follow(balance);
          return;
        }

        this.poll();
      }

      paint(balance) {
        this.stopPolling();
        if (this.labelTarget) this.labelTarget.textContent = balance.textContent.trim();
        this.hidden = false;
      }

      poll() {
        if (this.timer) return;

        let elapsed = 0;

        this.timer = setInterval(() => {
          elapsed += CartPointsRow.POLL_MS;
          const balance = this.findBalance();

          if (balance) {
            this.paint(balance);
            this.follow(balance);
          } else if (elapsed >= CartPointsRow.WAIT_MS) {
            this.stopPolling();
          }
        }, CartPointsRow.POLL_MS);
      }

      stopPolling() {
        if (!this.timer) return;
        clearInterval(this.timer);
        this.timer = null;
      }

      // Redeeming in the app's popup rewrites the figure in place, and the app re-renders its
      // widget when the cart changes, so the text is followed rather than read once. Scoped to the
      // widget the balance was found in, not the document, so this is not a page-wide observer.
      follow(balance) {
        if (this.observer) this.observer.disconnect();

        const widget = balance.closest('[id*="appstle" i], [class*="loyalty" i]') || balance;

        this.observer = new MutationObserver(() => {
          const current = this.findBalance();

          if (current) {
            this.paint(current);
            // The app can swap its whole widget rather than rewrite the text, which leaves this
            // observer watching a detached node — re-attach to whatever it renders next.
            if (current !== balance) this.follow(current);
            return;
          }

          this.poll();
        });

        this.observer.observe(widget, { childList: true, subtree: true, characterData: true });
      }
    }
  );
}
